import { Link, useNavigate } from "react-router-dom";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "./../../components/panel/panel.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import React, { useState, useRef, useCallback, useEffect } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import { updateUserData } from "../../app/auth/authSlice";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import ModifierAndProductMultiSelectionModal from "../../components/CustomComponents/modal/ModifierAndProductMultiSelectionModal.jsx";

function AddModifierGroup() {
  const dispatch = useDispatch();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { settingsData } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();



  const [name, setName] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [type, setType] = useState("List");
  const [canSkip, setCanSkip] = useState(true);
  const [enableQuantity, setEnableQuantity] = useState(true);
  const [minimumSelection, setMinimumSelection] = useState(0);
  const [maximumSelection, setMaximumSelection] = useState(0);
  const [freeSelection, setFreeSelection] = useState(0);
  const [extraSelectionCharges, setExtraSelectionCharges] = useState(0);
  const [settings, setSettings] = useState({ s_VAT: "", isTaxEnabled: false });
  const [modifiers, setModifiers] = useState([]);
  const [modals, setModals] = useState({
    showSelectItemsModal: false,
  });
  const [validationErrors, setValidationErrors] = useState({
    minimumSelection: "",
    maximumSelection: "",
  });

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const settingsRef = useRef({ s_VAT: "", isTaxEnabled: false });

  const safeParseFloat = (value, defaultVal = 0) => {
    if (value === null || value === undefined || value === "")
      return defaultVal;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultVal : parsed;
  };

  const safeParseInt = (value, defaultVal = 0) => {
    if (value === null || value === undefined || value === "")
      return defaultVal;
    const parsed = parseInt(value);
    return isNaN(parsed) ? defaultVal : parsed;
  };

  const validateSelectionFields = (minVal, maxVal) => {
    const errors = {
      minimumSelection: "",
      maximumSelection: "",
    };

    const minSel = parseInt(minVal) || 0;
    const maxSel = parseInt(maxVal) || 0;
    const modifierCount = Array.isArray(modifiers) ? modifiers.length : 0;

    if (maxSel > 0 && minSel > maxSel) {
      errors.minimumSelection = "Minimum selection cannot be greater than maximum selection";
    }

    if (maxSel > modifierCount && modifierCount > 0) {
      errors.maximumSelection = `Maximum selection cannot be greater than the number of modifiers (${modifierCount})`;
    }

    setValidationErrors(errors);
    return errors.minimumSelection === "" && errors.maximumSelection === "";
  };

  useEffect(() => {
    settingsRef.current = settings;

    if (settings.isTaxEnabled && settings.s_VAT) {
      setModifiers((prevModifiers) =>
        prevModifiers.map((modifier) => {
          const shouldUpdate =
            !modifier.menuItem_TaxPercentage ||
            modifier.menuItem_TaxPercentage === 0 ||
            modifier.menuItem_TaxPercentage === "0" ||
            modifier.menuItem_TaxPercentage === "";

          return {
            ...modifier,
            menuItem_TaxPercentage: shouldUpdate
              ? settings.s_VAT
              : modifier.menuItem_TaxPercentage,
            menuItem_SalePrice:
              modifier.menuItem_SalePrice === 0
                ? ""
                : modifier.menuItem_SalePrice,
            menuItem_CostPrice:
              modifier.menuItem_CostPrice === 0
                ? ""
                : modifier.menuItem_CostPrice,
            menuItem_DeliveryPrice:
              modifier.menuItem_DeliveryPrice === 0
                ? ""
                : modifier.menuItem_DeliveryPrice,
            menuItem_TakeAwayPrice:
              modifier.menuItem_TakeAwayPrice === 0
                ? ""
                : modifier.menuItem_TakeAwayPrice,
          };
        })
      );
    }
  }, [settings]);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      
      // First, try to get from Redux state
      if (settingsData) {
        const newSettings = {
          s_VAT: settingsData.s_VAT || 0,
          isTaxEnabled: settingsData.isTaxEnabled ?? false,
        };
        settingsRef.current = newSettings;
        setSettings(newSettings);
        setLoading(false);
        return;
      }

      // If not in Redux, try to fetch from API
      try {
        const response = await axiosInstance.get(APIs.GET_COMPANY_SETTINGS);
        if (response.data?.data) {
          const newSettings = {
            s_VAT: response.data.data.s_VAT || 0,
            isTaxEnabled: response.data.data.isTaxEnabled ?? false,
          };
          settingsRef.current = newSettings;
          setSettings(newSettings);
          // Update Redux with fetched data
          dispatch(updateUserData({ settingsData: response.data.data }));
          setLoading(false);
          return;
        }
      } catch (apiErr) {
        console.error("Failed to fetch settings from API:", apiErr);
      }

      // If both fail, use defaults but don't show error toast on initial load
      const defaultSettings = { s_VAT: 0, isTaxEnabled: false };
      settingsRef.current = defaultSettings;
      setSettings(defaultSettings);
    } catch (err) {
      console.error("Error in fetchSettings:", err);
      // Use defaults instead of showing error
      const defaultSettings = { s_VAT: 0, isTaxEnabled: false };
      settingsRef.current = defaultSettings;
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  }, [settingsData, dispatch]);

  useEffect(() => {
    const handler = setTimeout(async () => {
      await fetchSettings();
    }, 500);
    return () => clearTimeout(handler);
  }, [fetchSettings]);

  const handleModifierChange = (index, field, value) => {
    setModifiers((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      if (!arr[index]) return arr;

      if (field === "menuItem_IsDefaultModifier") {
        const willBeDefault = Boolean(value);
        return arr.map((m, i) => ({
          ...m,
          menuItem_IsDefaultModifier: i === index ? willBeDefault : false,
        }));
      }

      arr[index] = { ...arr[index], [field]: value };
      return arr;
    });
  };

  const handleRemoveModifier = (index) => {
    setModifiers((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const next = arr.filter((_, i) => i !== index);
      return next.map((m, i) => ({ ...m, SortingOrder: i + 1 }));
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (!name.trim()) {
        showErrorToast("Modifier Group Name is required");
        setLoading(false);
        return;
      }

      const updatedModifiers = Array.isArray(modifiers)
        ? modifiers.filter((m) => Number(m?.menuItem_ID) > 0 && m?.menuItem_Name)
        : [];

      if (updatedModifiers.length === 0) {
        showErrorToast("At least one add-on/product must be selected");
        setLoading(false);
        return;
      }

      // Validate minimumSelection and maximumSelection
      const minSel = parseInt(minimumSelection) || 0;
      const maxSel = parseInt(maximumSelection) || 0;
      const modifierCount = updatedModifiers.length;

      if (maxSel > 0 && minSel > maxSel) {
        showErrorToast("Minimum selection cannot be greater than maximum selection");
        setLoading(false);
        return;
      }

      if (maxSel > modifierCount) {
        showErrorToast(
          `Maximum selection (${maxSel}) cannot be greater than the number of modifiers (${modifierCount})`
        );
        setLoading(false);
        return;
      }

      const normalized = updatedModifiers
        .sort((a, b) => (parseInt(a.SortingOrder) || 0) - (parseInt(b.SortingOrder) || 0))
        .map((item, idx) => {
        const raw = item.raw || item;
        const sale = safeParseFloat(raw.menuItem_SalePrice ?? raw.MenuItem_SalePrice ?? raw.menuItem_Price ?? raw.MenuItem_Price);
        const delivery =
          raw.menuItem_DeliveryPrice === undefined ||
            raw.menuItem_DeliveryPrice === null ||
            raw.menuItem_DeliveryPrice === ""
            ? sale
            : safeParseFloat(raw.menuItem_DeliveryPrice ?? raw.MenuItem_DeliveryPrice);
        const takeAway =
          raw.menuItem_TakeAwayPrice === undefined ||
            raw.menuItem_TakeAwayPrice === null ||
            raw.menuItem_TakeAwayPrice === ""
            ? sale
            : safeParseFloat(raw.menuItem_TakeAwayPrice ?? raw.MenuItem_TakeAwayPrice);

        const reportCatId = raw.reportCat_ID || raw.rootCat_ID || "";
        const reportCatName = raw.reportCat_Name || raw.rootCat_Name || "";

        return {
          menuItem_ID: Number(item.menuItem_ID) || 0,
          menuItem_Name: item.menuItem_Name || "",
          allergens: raw.allergens || "",
          menuItem_IsModifier: Boolean(raw.menuItem_IsModifier ?? raw.MenuItem_IsModifier),
          menuItem_Type: raw.menuItem_Type || raw.MenuItem_Type || "",
          compID: CompID,
          itsMasterProductID: safeParseInt(raw.itsMasterProductID),
          menuItem_DeliveryPrice: delivery,
          menuItem_TakeAwayPrice: takeAway,
          menuItem_SalePrice: sale,
          menuItem_CostPrice: safeParseFloat(raw.menuItem_CostPrice ?? raw.MenuItem_CostPrice),
          menuItem_TaxPercentage: safeParseFloat(raw.menuItem_TaxPercentage ?? raw.MenuItem_TaxPercentage),
          reportCat_ID: reportCatId,
          reportCat_Name: reportCatName,
          is_OutOfStock: Boolean(raw.is_OutOfStock),
          menuItem_IsDefaultModifier: Boolean(item.menuItem_IsDefaultModifier),
          IsDefault: Boolean(item.menuItem_IsDefaultModifier),
          SortingOrder: idx + 1,
        };
      });

      const modifierGroupPayload = {
        CompID,
        modifierGroupID: 0,
        name: name,
        subTitle: subTitle,
        type: type,
        canSkip: canSkip,
        enableQuantity: enableQuantity,
        minimumSelection: parseInt(minimumSelection) || 0,
        maximumSelection: parseInt(maximumSelection) || 0,
        freeSelection: parseInt(freeSelection) || 0,
        extraSelectionCharges: parseFloat(extraSelectionCharges) || 0,
        detail: "",
        updateDatetime: new Date().toISOString(),
        Modifiers: normalized,
      };

      const response = await axiosInstance.post(
        APIs.POST_ADD_MODIFIER_GROUP,
        modifierGroupPayload
      );

      if (response.data.status) {
        showSuccessToast("Modifier Group added successfully");
        navigate("/Add-ons/Add-onsGroups");
      } else {
        showErrorToast(response.data.message || "Failed to add Modifier Group");
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.message ||
        "An error occurred while adding the Modifier Group"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const newModifiers = [...modifiers];
    const draggedModifier = newModifiers[draggedIndex];
    newModifiers.splice(draggedIndex, 1);
    newModifiers.splice(overIndex, 0, draggedModifier);
    setModifiers(newModifiers.map((m, i) => ({ ...m, SortingOrder: i + 1 })));

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const applySelectedItems = useCallback((selected) => {
    const arr = Array.isArray(selected) ? selected : [];
    const normalized = arr
      .filter((x) => Number(x?.menuItem_ID) > 0 && x?.menuItem_Name)
      .map((x, i) => ({
        ...x,
        menuItem_IsDefaultModifier: Boolean(x.menuItem_IsDefaultModifier),
        SortingOrder: i + 1,
      }));
    setModifiers(normalized);
  }, []);

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/Add-ons/Add-onsGroups">
                Manage Add-on Groups
              </Link>
            </li>
            <li className="breadcrumb-item active">Add Add-on Group</li>
          </ol>
          <h1 className="page-header mb-0">Add Add-on Group</h1>
        </div>
        <div className="ms-auto">
          <button
            onClick={handleSubmit}
            className="btn px-4"
            disabled={!name.trim() || !type}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: `0 2px 8px ${themeColor}40`
            }}
            onMouseEnter={(e) => {
              if (!e.target.disabled) {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }
            }}
            onMouseLeave={(e) => {
              if (!e.target.disabled) {
                e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }
            }}
          >
            <i className="fa-lg me-2 ms-n2 text-white"></i>
            Submit
          </button>
        </div>
      </div>

      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>Add-on Group Details</PanelHeader>
          <PanelBody>
            <div className="p-2">
              <div className="row mb-4">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label={"Name"}
                      placeHolder={"Enter Name"}
                      type={"text"}
                      value={name}
                      onchange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label={"Subtitle"}
                      placeHolder={"Enter Subtitle"}
                      type={"text"}
                      value={subTitle}
                      onchange={(e) => setSubTitle(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label={"Minimum Selection"}
                      placeHolder={"Enter Minimum Selection"}
                      type={"number"}
                      value={minimumSelection}
                      onchange={(e) => {
                        setMinimumSelection(e.target.value);
                        validateSelectionFields(e.target.value, maximumSelection);
                      }}
                      error={validationErrors.minimumSelection}
                    />
                    {validationErrors.minimumSelection && (
                      <small className="text-danger d-block mt-1">
                        {validationErrors.minimumSelection}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label={"Maximum Selection"}
                      placeHolder={"Enter Maximum Selection"}
                      type={"number"}
                      value={maximumSelection}
                      onchange={(e) => {
                        setMaximumSelection(e.target.value);
                        validateSelectionFields(minimumSelection, e.target.value);
                      }}
                      error={validationErrors.maximumSelection}
                    />
                    {validationErrors.maximumSelection && (
                      <small className="text-danger d-block mt-1">
                        {validationErrors.maximumSelection}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label={"Free Selection"}
                      placeHolder={"Enter Free Selection Count"}
                      type={"number"}
                      value={freeSelection}
                      onchange={(e) => setFreeSelection(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <ToggleSwitch
                      isChecked={canSkip}
                      onToggle={() => setCanSkip(!canSkip)}
                      label={"Can Skip"}
                      disabled={false}
                      themeColor={themeColor}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <ToggleSwitch
                      isChecked={enableQuantity}
                      onToggle={() => setEnableQuantity(!enableQuantity)}
                      label={"Enable Quantity"}
                      disabled={false}
                      themeColor={themeColor}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Add-ons & Products</h5>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() =>
                        setModals((prev) => ({ ...prev, showSelectItemsModal: true }))
                      }
                      style={{
                        backgroundColor: themeColor,
                        border: "none",
                        borderRadius: "8px",
                        padding: "8px 16px",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Select Items
                    </button>
                  </div>

                  <table className="table table-responsive">
                    <thead style={{ backgroundColor: "#d9d9d9" }}>
                      <tr>
                        <th>Item</th>
                        <th>Type</th>
                        <th>Cost Price</th>
                        <th>Sale Price</th>
                        <th>Default</th>
                        <th>Sort Order</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modifiers.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            No items selected. Click <strong>Select Items</strong> to add add-ons/products.
                          </td>
                        </tr>
                      ) : modifiers.map((modifier, index) => (
                        <React.Fragment key={`${modifier.menuItem_ID}-${index}`}>
                          <tr
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragEnter={(e) => handleDragEnter(e, index)}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onDragEnd={handleDragEnd}
                          >
                            <td className="align-middle">
                              <span style={{ fontWeight: 600 }}>
                                {modifier.menuItem_Name}
                              </span>
                            </td>

                            <td className="align-middle">
                              <span className="badge bg-secondary">
                                {(modifier.raw?.menuItem_Type || modifier.raw?.MenuItem_Type || modifier.menuItem_Type || "Item") === "Modifier"
                                  ? "Add-on"
                                  : "Product"}
                              </span>
                            </td>

                            <td className="align-middle">
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: `${themeColor}10`,
                                  color: isDarkMode ? "#e2e8f0" : "#495057",
                                  fontWeight: 600,
                                  padding: "8px 10px",
                                }}
                              >
                                {(() => {
                                  const raw = modifier.raw || modifier;
                                  const v =
                                    raw?.menuItem_CostPrice ??
                                    raw?.MenuItem_CostPrice ??
                                    raw?.menuItem_Cost ??
                                    raw?.MenuItem_Cost ??
                                    0;
                                  const n = parseFloat(v);
                                  return isNaN(n) ? "0.00" : n.toFixed(2);
                                })()}
                              </span>
                            </td>

                            <td className="align-middle">
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: `${themeColor}10`,
                                  color: isDarkMode ? "#e2e8f0" : "#495057",
                                  fontWeight: 600,
                                  padding: "8px 10px",
                                }}
                              >
                                {(() => {
                                  const raw = modifier.raw || modifier;
                                  const v =
                                    raw?.menuItem_SalePrice ??
                                    raw?.MenuItem_SalePrice ??
                                    raw?.menuItem_Price ??
                                    raw?.MenuItem_Price ??
                                    0;
                                  const n = parseFloat(v);
                                  return isNaN(n) ? "0.00" : n.toFixed(2);
                                })()}
                              </span>
                            </td>

                            <td className="align-middle">
                              <ToggleSwitch
                                isChecked={Boolean(modifier.menuItem_IsDefaultModifier)}
                                label="Default"
                                onToggle={() =>
                                  handleModifierChange(
                                    index,
                                    "menuItem_IsDefaultModifier",
                                    !modifier.menuItem_IsDefaultModifier
                                  )
                                }
                                themeColor={themeColor}
                              />
                            </td>

                            <td className="align-middle">
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: `${themeColor}15`,
                                  color: themeColor,
                                  fontWeight: 700,
                                  padding: "8px 10px",
                                }}
                              >
                                {index + 1}
                              </span>
                            </td>

                            <td className="align-middle">
                              <div className="d-flex align-items-center m-1">
                                <i
                                  className="fa fa-trash text-danger m-3"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleRemoveModifier(index)}
                                ></i>
                                <i
                                  className="fa fa-bars m-3"
                                  style={{ cursor: "grab" }}
                                ></i>
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>

      <ModifierAndProductMultiSelectionModal
        showModal={modals.showSelectItemsModal}
        setShowModal={(val) =>
          setModals((prev) => ({ ...prev, showSelectItemsModal: val }))
        }
        compID={CompID}
        selectedItems={modifiers}
        onSubmit={applySelectedItems}
      />
    </div>
  );
}

export default AddModifierGroup;